import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { trackUserBehavior } from '../utils/analytics';

const ChatContainer = styled(motion.div)`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
`;

const ChatButton = styled(motion.button)`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px ${props => props.theme.colors.primary}40;
  color: ${props => props.theme.colors.dark};
  font-size: 24px;
  position: relative;

  &:hover {
    transform: scale(1.1);
  }
`;

const ChatWindow = styled(motion.div)`
  position: absolute;
  bottom: 80px;
  right: 0;
  width: 350px;
  height: 500px;
  background: ${props => props.theme.colors.darkGray};
  border-radius: 16px;
  border: 1px solid ${props => props.theme.colors.mediumGray};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 300px;
    height: 400px;
  }
`;

const ChatHeader = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: ${props => props.theme.colors.dark};
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ChatTitle = styled.div`
  font-weight: 600;
  font-size: 1rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.dark};
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ChatMessages = styled.div`
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.mediumGray};
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.primary};
    border-radius: 2px;
  }
`;

const Message = styled(motion.div)`
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 16px;
  font-size: 0.9rem;
  line-height: 1.4;
  align-self: ${props => props.isUser ? 'flex-end' : 'flex-start'};
  background: ${props => props.isUser 
    ? `linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.secondary})`
    : props.theme.colors.mediumGray};
  color: ${props => props.isUser ? props.theme.colors.dark : props.theme.colors.white};
`;

const QuickReplies = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const QuickReply = styled.button`
  background: transparent;
  border: 1px solid ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.primary}20;
  }
`;

const ChatInput = styled.div`
  padding: 16px;
  border-top: 1px solid ${props => props.theme.colors.mediumGray};
  display: flex;
  gap: 8px;
`;

const Input = styled.input`
  flex: 1;
  background: ${props => props.theme.colors.mediumGray};
  border: 1px solid ${props => props.theme.colors.mediumGray};
  border-radius: 20px;
  padding: 10px 16px;
  color: ${props => props.theme.colors.white};
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &::placeholder {
    color: ${props => props.theme.colors.lightGray};
  }
`;

const SendButton = styled.button`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${props => props.theme.colors.dark};

  &:hover {
    transform: scale(1.05);
  }
`;

const TypingIndicator = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 16px;
  background: ${props => props.theme.colors.mediumGray};
  border-radius: 16px;
  align-self: flex-start;
  max-width: 80px;
`;

const TypingDot = styled(motion.div)`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => props.theme.colors.lightGray};
`;

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Olá! Sou o assistente do Leonardo Celini. Como posso ajudá-lo com o CCP - Consignação com Propósito?",
      isUser: false,
      quickReplies: [
        "Sobre o CCP",
        "Preços e formas de pagamento",
        "Garantia",
        "Suporte"
      ]
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const botResponses = {
    "sobre o ccp": {
      text: "O CCP é o método Consignação com Propósito criado pelo Leonardo Celini. Você aprenderá técnicas práticas para consignar veículos com segurança e lucro.",
      quickReplies: ["O que vou aprender?", "Quanto tempo dura?", "Preços"]
    },
    "o que vou aprender": {
      text: "Você aprenderá 3 módulos: 1) Fundamentos da LM Motors, 2) Marketing e Negociação, 3) Consignação Lucrativa. Tudo com foco prático e aplicável.",
      quickReplies: ["Preços", "Garantia", "Quero comprar"]
    },
    "quanto tempo dura": {
      text: "O curso é 100% online com acesso vitalício. Você pode fazer no seu ritmo. A maioria dos alunos completa em 2-4 semanas.",
      quickReplies: ["Preços", "O que vou aprender?", "Garantia"]
    },
    "preços": {
      text: "🔥 OFERTA BLACK FRIDAY: De R$ 1.497 por apenas R$ 497 com cupom CCPBLACK! Pagamento único com acesso vitalício. Oferta limitada!",
      quickReplies: ["🛒 QUERO COMPRAR AGORA", "Garantia", "Formas de pagamento"]
    },
    "preços e formas de pagamento": {
      text: "💥 R$ 497 com cupom CCPBLACK (era R$ 1.497)! Cartão até 12x, PIX à vista ou boleto. Processamento seguro pela CursEduca.",
      quickReplies: ["🛒 COMPRAR COM DESCONTO", "Garantia", "Mais detalhes"]
    },
    "formas de pagamento": {
      text: "Aceitamos: Cartão de crédito (parcelamento até 12x), PIX à vista, e boleto bancário. Todas as formas são seguras e processadas pela Hotmart.",
      quickReplies: ["Quero comprar", "Preços", "Garantia"]
    },
    "garantia": {
      text: "✅ Garantia incondicional de 7 dias! Se não ficar satisfeito, devolvemos 100% do seu dinheiro sem perguntas. Risco zero para você!",
      quickReplies: ["🛒 COMPRAR COM GARANTIA", "Como funciona?", "Sobre Leonardo"]
    },
    "como funciona": {
      text: "Após a compra, você recebe acesso imediato à plataforma com todos os módulos em vídeo, materiais e suporte. Tudo online e vitalício.",
      quickReplies: ["Quero comprar", "Suporte", "Preços"]
    },
    "suporte": {
      text: "Suporte completo incluído! Você terá acesso a suporte técnico e tira-dúvidas sobre o conteúdo durante todo o curso.",
      quickReplies: ["Quero comprar", "Mais informações", "Garantia"]
    },
    "sobre leonardo": {
      text: "Leonardo Celini tem mais de 15 anos de experiência no mercado automotivo, é fundador da LM Motors e criador do método CCP.",
      quickReplies: ["Quero comprar", "Sobre o CCP", "Preços"]
    },
    "quero comprar": {
      text: "🚀 Perfeito! Redirecionando para o checkout seguro... Use o cupom CCPBLACK para garantir o desconto de R$ 1.000!",
      action: "redirect_checkout",
      quickReplies: ["Garantia", "Formas de pagamento", "Suporte"]
    },
    "🛒 quero comprar agora": {
      text: "🎯 Excelente escolha! Redirecionando para o checkout... Lembre-se: cupom CCPBLACK para R$ 497!",
      action: "redirect_checkout",
      quickReplies: []
    },
    "🛒 comprar com desconto": {
      text: "💰 Ótima decisão! Levando você direto para o checkout com desconto aplicado...",
      action: "redirect_checkout",
      quickReplies: []
    },
    "🛒 comprar com garantia": {
      text: "🔒 Compra 100% segura! Redirecionando para checkout com garantia de 7 dias...",
      action: "redirect_checkout",
      quickReplies: []
    },
    "mais detalhes": {
      text: "📚 O CCP inclui: Módulos em vídeo HD, suporte por 6 meses, certificado, acesso vitalício e garantia de 7 dias. Tudo por R$ 497!",
      quickReplies: ["🛒 QUERO COMPRAR AGORA", "Preços", "Garantia"]
    },
    "mais informações": {
      text: "O curso é 100% prático, baseado na experiência real do Leonardo na LM Motors. Você aprende o passo a passo para consignar com segurança e lucro.",
      quickReplies: ["O que vou aprender?", "Quero comprar", "Preços"]
    },
    "falar com especialista": {
      text: "Para falar diretamente com nossa equipe, use o WhatsApp no rodapé da página ou envie um email. Teremos prazer em ajudar!",
      quickReplies: ["Quero comprar", "Preços", "Sobre o CCP"]
    },
    "ver preços": {
      text: "🏷️ BLACK FRIDAY: R$ 497 (era R$ 1.497) com cupom CCPBLACK! Melhor investimento para seu negócio automotivo!",
      quickReplies: ["🛒 APROVEITAR OFERTA", "Formas de pagamento", "Garantia"]
    },
    "🛒 aproveitar oferta": {
      text: "⚡ Oferta imperdível! Redirecionando para garantir seu desconto...",
      action: "redirect_checkout",
      quickReplies: []
    },
    "default": {
      text: "Entendo! Estou aqui para ajudar com qualquer dúvida sobre o CCP. O que gostaria de saber?",
      quickReplies: ["Sobre o CCP", "🔥 Ver Oferta", "Garantia", "🛒 Quero comprar"]
    },
    "🔥 ver oferta": {
      text: "🎉 OFERTA BLACK FRIDAY: R$ 497 (era R$ 1.497)! Use cupom CCPBLACK. Oferta por tempo limitado!",
      quickReplies: ["🛒 COMPRAR AGORA", "Garantia", "Mais detalhes"]
    },
    "🛒 comprar agora": {
      text: "🚀 Redirecionando para o checkout seguro... Aproveite o desconto!",
      action: "redirect_checkout",
      quickReplies: []
    }
  };

  const handleSendMessage = (text = inputValue) => {
    if (!text.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: text,
      isUser: true
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Track user interaction
    trackUserBehavior.trackEvent('chatbot_message', {
      message: text,
      timestamp: new Date().toISOString()
    });

    // Simulate bot response
    setTimeout(() => {
      setIsTyping(false);
      const lowerText = text.toLowerCase();
      let response = botResponses.default;

      // Simple keyword matching
      for (const [key, value] of Object.entries(botResponses)) {
        if (lowerText.includes(key)) {
          response = value;
          break;
        }
      }

      // Handle checkout redirect
      if (response.action === 'redirect_checkout') {
        setTimeout(() => {
          window.location.href = 'https://pay.curseduca.com/leocelini/p/consignacao-com-proposito-1757530675544';
        }, 1000);
      }

      const botMessage = {
        id: Date.now() + 1,
        text: response.text,
        isUser: false,
        quickReplies: response.quickReplies
      };

      setMessages(prev => [...prev, botMessage]);
    }, 1500);
  };

  const handleQuickReply = (reply) => {
    handleSendMessage(reply);
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      trackUserBehavior.trackEvent('chatbot_opened', {
        timestamp: new Date().toISOString()
      });
    }
  };

  return (
    <ChatContainer>
      <AnimatePresence>
        {isOpen && (
          <ChatWindow
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.2 }}
          >
            <ChatHeader>
              <ChatTitle>Assistente Leonardo Celini</ChatTitle>
              <CloseButton onClick={toggleChat}>×</CloseButton>
            </ChatHeader>

            <ChatMessages>
              {messages.map((message) => (
                <div key={message.id}>
                  <Message
                    isUser={message.isUser}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {message.text}
                  </Message>
                  {message.quickReplies && (
                    <QuickReplies>
                      {message.quickReplies.map((reply, index) => (
                        <QuickReply
                          key={index}
                          onClick={() => handleQuickReply(reply)}
                        >
                          {reply}
                        </QuickReply>
                      ))}
                    </QuickReplies>
                  )}
                </div>
              ))}
              
              {isTyping && (
                <TypingIndicator
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <TypingDot
                    animate={{ y: [0, -4, 0] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                  />
                  <TypingDot
                    animate={{ y: [0, -4, 0] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                  />
                  <TypingDot
                    animate={{ y: [0, -4, 0] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                  />
                </TypingIndicator>
              )}
              <div ref={messagesEndRef} />
            </ChatMessages>

            <ChatInput>
              <Input
                type="text"
                placeholder="Digite sua mensagem..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              />
              <SendButton onClick={() => handleSendMessage()}>
                →
              </SendButton>
            </ChatInput>
          </ChatWindow>
        )}
      </AnimatePresence>

      <ChatButton
        onClick={toggleChat}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        {isOpen ? '×' : '💬'}
      </ChatButton>
    </ChatContainer>
  );
};

export default Chatbot;